import React, { useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { MultiSelectDropdownWithField } from '@/components/ui/dropdowns';
import { getCachedTypes } from '@/lib/stores/typesStore';
import { getCachedTemplates } from '@/lib/stores/templatesStore';

interface DialogBasicInfoFormProps {
  description: string;
  translatable: boolean;
  speakerMode: 'none' | 'type' | 'free-text' | 'object';
  speakerTypeId?: string | null;
  speakerTemplateIds?: string[];
  gameId?: string;
  onDescriptionChange: (value: string) => void;
  onTranslatableChange: (value: boolean) => void;
  onSpeakerModeChange: (value: 'none' | 'type' | 'free-text' | 'object') => void;
  onSpeakerTypeIdChange: (value: string | null) => void;
  onSpeakerTemplateIdsChange: (value: string[]) => void;
  t: any;
}

export function DialogBasicInfoForm({
  description,
  translatable,
  speakerMode,
  speakerTypeId,
  speakerTemplateIds,
  gameId,
  onDescriptionChange,
  onTranslatableChange,
  onSpeakerModeChange,
  onSpeakerTypeIdChange,
  onSpeakerTemplateIdsChange,
  t,
}: DialogBasicInfoFormProps) {
  // Get types and templates for speaker configuration
  const types = gameId ? getCachedTypes(gameId) : [];
  const allTemplates = gameId ? getCachedTemplates(gameId) : [];
  
  // Filter to only show glossary templates (not document templates) for speaker selection
  // Documents don't make sense as speakers - speakers should be characters/NPCs/entities
  const templates = allTemplates.filter(t => t.type === 'glossary' || !t.type);


  // Reset dependent fields when speaker mode changes
  useEffect(() => {
    if (speakerMode === 'none' || speakerMode === 'free-text') {
      onSpeakerTypeIdChange(null);
      onSpeakerTemplateIdsChange([]);
    } else if (speakerMode === 'type') {
      onSpeakerTemplateIdsChange([]);
    } else if (speakerMode === 'object') {
      onSpeakerTypeIdChange(null);
    }
  }, [speakerMode]);

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="description">{t('platform.common.description')}</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder={t('platform.wizards.dialog.enter_dialog_description')}
          rows={2}
        />
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="translatable"
          checked={translatable}
          onCheckedChange={(checked) => onTranslatableChange(checked === true)}
        />
        <Label htmlFor="translatable" className="cursor-pointer">
          Add all texts to Translation Management System (TMS)
        </Label>
      </div>

      {/* Speaker Configuration */}
      <div className="space-y-3 pt-2 border-t">
        <div>
          <Label htmlFor="speaker-mode" className="text-sm font-medium">
            Character/Speaker Attribution
          </Label>
          <p className="text-xs text-muted-foreground mb-2">
            Configure how characters are assigned to dialog text
          </p>
          <Select value={speakerMode} onValueChange={onSpeakerModeChange}>
            <SelectTrigger id="speaker-mode" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None - No speaker attribution</SelectItem>
              <SelectItem value="free-text">Free Text - Type any name</SelectItem>
              <SelectItem value="type">Type - Select from a type list</SelectItem>
              <SelectItem value="object">Object - Select from templates</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Show type selector if mode is 'type' */}
        {speakerMode === 'type' && (
          <div>
            <Label htmlFor="speaker-type" className="text-sm">
              Select Type *
            </Label>
            <Select 
              value={speakerTypeId || ''} 
              onValueChange={(value) => onSpeakerTypeIdChange(value || null)}
            >
              <SelectTrigger id="speaker-type" className="w-full">
                <SelectValue placeholder="Choose a type..." />
              </SelectTrigger>
              <SelectContent>
                {types.length === 0 ? (
                  <SelectItem value="__empty" disabled>
                    No types available
                  </SelectItem>
                ) : (
                  types.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Show template selector if mode is 'object' */}
        {speakerMode === 'object' && (
          <div>
            <Label className="text-sm">
              Select Template(s) *
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Characters can be selected from objects using these templates
            </p>
            <MultiSelectDropdownWithField
              value={speakerTemplateIds || []}
              onChange={(value) => onSpeakerTemplateIdsChange(Array.isArray(value) ? value : [value])}
              options={templates.map(t => ({ id: t.id, label: t.name }))}
              placeholder="Select templates..."
              multiple
            />
          </div>
        )}
      </div>
    </div>
  );
}

